import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  archiveWidget,
  listWidgets,
  setDefaultWidget,
  type Widget,
} from '../api/widgets.js';
import { ApiError } from '../api/apiClient.js';
import { ConfirmModal } from '../components/ConfirmModal.js';
import { useUiStore } from '../state/uiStore.js';
import { t } from '../i18n/es.js';

export function WidgetsList(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const [includeArchived, setIncludeArchived] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<Widget | null>(null);
  const pushToast = useUiStore((s) => s.pushToast);
  const setActiveTenant = useUiStore((s) => s.setActiveTenant);
  const activeTenantId = useUiStore((s) => s.activeTenantId);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (id && id !== activeTenantId) setActiveTenant(id);
  }, [id, activeTenantId, setActiveTenant]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['widgets', id, { includeArchived }],
    queryFn: () => listWidgets(id, { includeArchived }),
    enabled: Boolean(id),
  });

  const archiveMutation = useMutation({
    mutationFn: (widgetId: string) => archiveWidget(id, widgetId),
    onSuccess: () => {
      pushToast('success', t.widget.archived);
      queryClient.invalidateQueries({ queryKey: ['widgets', id] });
      setConfirmTarget(null);
    },
    onError: (e: unknown) => {
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      pushToast('error', msg);
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: (widgetId: string) => setDefaultWidget(id, widgetId),
    onSuccess: () => {
      pushToast('success', t.widget.setDefaultSuccess);
      queryClient.invalidateQueries({ queryKey: ['widgets', id] });
    },
    onError: (e: unknown) => {
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      pushToast('error', msg);
    },
  });

  const widgets = data ?? [];

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold">{t.widget.listTitle}</h2>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            {t.widget.showArchived}
          </label>
          <Link
            to={`/tenants/${id}/widgets/new`}
            className="rounded bg-brand-500 px-3 py-1 text-sm text-white hover:bg-brand-600"
          >
            {t.widget.newBtn}
          </Link>
        </div>
      </div>

      {isLoading && <p className="text-slate-500">{t.app.loading}</p>}
      {error && <p className="text-red-700">{(error as Error).message}</p>}
      {!isLoading && !error && widgets.length === 0 && (
        <p className="text-slate-500">{t.widget.empty}</p>
      )}

      {widgets.length > 0 && (
        <div className="overflow-x-auto rounded border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">{t.widget.fields.name}</th>
                <th className="px-4 py-2">{t.widget.fields.isDefault}</th>
                <th className="px-4 py-2">{t.widget.fields.updated}</th>
                <th className="px-4 py-2">{t.widget.fields.version}</th>
                <th className="px-4 py-2 text-right">{t.widget.fields.actions}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {widgets.map((w) => (
                <tr
                  key={w.id}
                  data-testid={`widget-row-${w.id}`}
                  className={w.deletedAt ? 'opacity-50' : undefined}
                >
                  <td className="px-4 py-2 font-medium">
                    <span
                      className="inline-block h-3 w-3 rounded-full align-middle"
                      style={{ background: w.config.primaryColor }}
                      aria-hidden
                    />{' '}
                    {w.name}
                  </td>
                  <td className="px-4 py-2">
                    {w.isDefault ? (
                      <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
                        ⭐ {t.widget.isDefault}
                      </span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-slate-500">
                    {new Date(w.updatedAt).toLocaleString()}
                  </td>
                  <td className="px-4 py-2 text-slate-500">v{w.version}</td>
                  <td className="px-4 py-2 text-right">
                    {!w.deletedAt && (
                      <div className="flex justify-end gap-2">
                        {!w.isDefault && (
                          <button
                            type="button"
                            onClick={() => setDefaultMutation.mutate(w.id)}
                            disabled={setDefaultMutation.isPending}
                            className="rounded border border-amber-300 px-2 py-1 text-xs text-amber-800 hover:bg-amber-50"
                          >
                            {t.widget.setDefault}
                          </button>
                        )}
                        <Link
                          to={`/tenants/${id}/widgets/${w.id}/edit`}
                          className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50"
                        >
                          {t.widget.edit}
                        </Link>
                        <button
                          type="button"
                          onClick={() => setConfirmTarget(w)}
                          className="rounded border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                        >
                          {t.widget.archive}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        open={confirmTarget !== null}
        title={t.widget.confirmArchiveTitle}
        description={t.widget.confirmArchiveDesc}
        confirmLabel={t.widget.archive}
        cancelLabel={t.app.cancel}
        loading={archiveMutation.isPending}
        onConfirm={() => {
          if (confirmTarget) archiveMutation.mutate(confirmTarget.id);
        }}
        onCancel={() => setConfirmTarget(null)}
      />
    </div>
  );
}
